import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Loader2 } from 'lucide-react';

const LoginPage = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [database, setDatabase] = useState('test');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, database })
      });

      const data = await response.json();

      if (data.success && data.token) {
        localStorage.setItem('auth_token', data.token);
        onLogin(data.token, data.environment || database);
      } else {
        setError(data.message || 'Invalid password');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="mb-8 flex justify-center">
          <img 
            src="/suteko-logo.png" 
            alt="Suteko Steel" 
            className="h-20 object-contain"
          />
        </div>

        <Card className="bg-zinc-900 border-zinc-800 shadow-2xl">
          <CardContent className="p-6">
            <div className="text-center mb-6">
              <h1 className="text-xl font-bold text-white">Suteko Steel</h1>
              <p className="text-sm text-zinc-400 mt-1">Enter password to access</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-zinc-400 mb-1.5">Database</label>
                <div className="grid grid-cols-2 gap-2" data-testid="login-db-selector">
                  <button
                    type="button"
                    onClick={() => setDatabase('test')}
                    className={`px-3 py-2 rounded-md text-sm font-medium border transition-colors ${
                      database === 'test'
                        ? 'bg-amber-500/15 border-amber-500/60 text-amber-300'
                        : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:text-zinc-200'
                    }`}
                    data-testid="login-db-test"
                  >
                    🧪 Test data
                  </button>
                  <button
                    type="button"
                    onClick={() => setDatabase('production')}
                    className={`px-3 py-2 rounded-md text-sm font-medium border transition-colors ${
                      database === 'production'
                        ? 'bg-rose-500/15 border-rose-500/60 text-rose-300'
                        : 'bg-zinc-800 border-zinc-700 text-zinc-400 hover:text-zinc-200'
                    }`}
                    data-testid="login-db-prod"
                  >
                    🔒 Production
                  </button>
                </div>
                {database === 'production' && (
                  <p className="text-[11px] text-rose-400/80 mt-1.5">
                    Production database — real records. Be careful.
                  </p>
                )}
              </div>

              <div>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-zinc-500"
                  autoFocus
                  data-testid="login-password-input"
                />
              </div>

              {error && (
                <p className="text-sm text-red-400 text-center" data-testid="login-error">
                  {error}
                </p>
              )}

              <Button
                type="submit"
                className="w-full bg-white hover:bg-zinc-200 text-black font-semibold"
                disabled={loading || !password}
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Enter'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-zinc-600 text-xs mt-6">
          Suteko Steel™ Internal Tool
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
